// @flow

import React, { Component, Suspense } from 'react';
import { connect } from 'react-redux';
import { Switch, withRouter, Redirect, Route } from 'react-router-dom';
import Layout from '../modules/Layout';
import Error404 from '../components/ErrorPages/Error404';
import Error500 from '../components/ErrorPages/Error500';
import AuthRoute from '../components/AuthRoute';
import { ThemeProvider } from 'styled-components/macro';
import GlobalStyles from '../globalStyles';
import Fonts from '../styles/fonts';
import theme from '../styles/theme';
import PageLoader from '../components/PageLoader';
import NoAuthRoute from '../components/NoAuthRoute';
import { type Partner } from '../utilities/types';
import SignIn from './SignIn';
import Dashboard from './Dashboard';
import {
  refreshSession,
  routeChanged,
  handleAppLoad,
  setViewportDevice
} from './reducer';
import CommissionPayouts from './CommissionPayouts';
import Account from './Account';
import Marketing from './Marketing';
import ModalConductor from '../modules/ModalConductor';
import PasswordReset from './PasswordReset';
import Merchants from './Merchants';
import Partners from './Partners';
import withBackgroundState from '../utilities/hoc/WithBackgroundState';
import Support from './Support';

type Props = {
  location: Location,
  partner: Partner,
  loading: boolean,
  sidebarOpened: boolean,
  handleAppLoad: () => void,
  refreshSession: () => void,
  routeChanged: (prevRoute: ?string, nextRoute: string) => void,
  setViewportDevice: boolean => void,
  viewportDevice: boolean,
  flippers: {
    partner_referrals: boolean
  }
};

class App extends Component<Props> {
  sessionInterval: IntervalID;

  componentDidMount() {
    this.initializeRoutes();
    this.initializeViewport();
    this.props.handleAppLoad();
    this.sessionInterval = setInterval(() => {
      if (this.props.partner) {
        this.props.refreshSession();
      }
    }, 10 * 60 * 1000);
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    clearInterval(this.sessionInterval);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      const basePath = this.props.location.pathname.split('/')[1];
      const prevBasePath = prevProps.location.pathname.split('/')[1];
      if (
        basePath !== prevBasePath ||
        this.props.location.pathname.endsWith('new')
      ) {
        window.scrollTo(0, 0);
      }
      this.props.routeChanged(
        prevProps.location.pathname,
        this.props.location.pathname
      );
    }
  }

  initializeRoutes = () => {
    this.props.routeChanged(null, this.props.location.pathname);
  };

  initializeViewport = () => {
    if (window.innerWidth < 1024) {
      this.props.setViewportDevice(true);
    } else {
      this.props.setViewportDevice(false);
    }
  };

  handleResize = () => {
    if (window.innerWidth < 1024) {
      if (!this.props.viewportDevice) {
        this.props.setViewportDevice(true);
      }
    }
    if (window.innerWidth >= 1024) {
      if (this.props.viewportDevice) {
        this.props.setViewportDevice(false);
      }
    }
  };

  render() {
    const isAuthenticated = Boolean(this.props.partner);
    if (this.props.loading) {
      return <PageLoader />;
    }
    return (
      <ThemeProvider theme={theme({ sidebarOpened: this.props.sidebarOpened })}>
        <>
          <GlobalStyles />
          <Fonts />
          <Layout currentRoute={this.props.location.pathname}>
            <Suspense fallback={<PageLoader />}>
              <Switch>
                <NoAuthRoute
                  path="/password_resets"
                  component={PasswordReset}
                  isAuthenticated={isAuthenticated}
                />
                <AuthRoute
                  exact
                  path="/"
                  component={Dashboard}
                  isAuthenticated={isAuthenticated}
                />
                <AuthRoute
                  path="/commission-payouts"
                  component={CommissionPayouts}
                  isAuthenticated={isAuthenticated}
                />
                <NoAuthRoute
                  exact
                  path="/signin"
                  component={SignIn}
                  isAuthenticated={isAuthenticated}
                />
                <AuthRoute
                  path="/merchants"
                  component={Merchants}
                  isAuthenticated={isAuthenticated}
                />
                <AuthRoute
                  path="/account"
                  component={Account}
                  isAuthenticated={isAuthenticated}
                />

                <AuthRoute
                  path="/marketing"
                  component={Marketing}
                  isAuthenticated={isAuthenticated}
                />
                <AuthRoute
                  path="/support"
                  component={Support}
                  isAuthenticated={isAuthenticated}
                />
                {this.props.flippers.partner_referrals && (
                  <AuthRoute
                    path="/partners"
                    component={Partners}
                    isAuthenticated={isAuthenticated}
                  />
                )}

                <Route path="/error" component={Error500} />
                {/* <NoAuthRoute path="/maintenance" component={Maintenance} /> */}
                <Route path="/page_not_found" component={Error404} />
                <Redirect to="/page_not_found" />
              </Switch>
            </Suspense>
          </Layout>
          <ModalConductor />
        </>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = state => ({
  partner: state.global.partner,
  loading: state.global.loading,
  sidebarOpened: state.global.sidebarOpened,
  viewportDevice: state.global.viewportDevice,
  flippers: state.global.flippers
});

const mapDispatchToProps = {
  handleAppLoad,
  refreshSession,
  routeChanged,
  setViewportDevice
};

export default withBackgroundState(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(App))
);
